import * as React from 'react';
import { renderSwitch } from '../../../utils/react';
import { defined } from '../../../utils/variableEvaluation';
import { InputCheckbox } from '../Checkbox';
import CopyInput from '../CopyInput';
import { InputDate } from '../Date';
import InputFile from '../File';
import { EInputTextType, IInputConfig } from '../index';
import { InputRadio } from '../Radio';
import { InputSelect } from '../Select/input';
import { Switch } from '../Switch';
import { InputText } from '../Text';
import { Textarea } from '../Textarea';

export enum EInputType {
  Textarea = 'Textarea',
  File = 'File',
  Text = 'Text',
  Checkbox = 'Checkbox',
  Radio = 'Radio',
  Select = 'Select',
  Date = 'Date',
  Switch = 'Switch',
  Custom = 'Custom',
  Copy = 'Copy',
}

interface IProps {
  inputConfig: IInputConfig;
}

export class InputContent extends React.Component<IProps, {}> {
  public render(): JSX.Element {
    const {
      checkboxLabel,
      checked,
      clearable,
      cols,
      customComponent,
      disabled,
      filtering,
      file,
      forcedValue,
      id,
      inputTextType,
      isFocused,
      isLoading,
      isOpen,
      max,
      maxLength,
      min,
      minDate,
      maxDate,
      name,
      onBlur,
      onChange,
      onKeyDown,
      onDropdownOpen,
      onFocus,
      onSelected,
      placeholder,
      radioOptions,
      readOnly,
      required,
      rows,
      selectOptions,
      step,
      type,
      value,
      size,
      switchType,
      defaultChecked,
      style,
      radioExtraStyles,
    } = this.props.inputConfig;

    return renderSwitch(this.inputType(), {
      [EInputType.Custom]: () => customComponent!,
      [EInputType.Checkbox]: () => (
        <InputCheckbox
          isFocused={isFocused}
          isLoading={isLoading}
          name={name}
          value={value}
          checked={checked}
          readOnly={readOnly}
          label={checkboxLabel}
          type={type!}
          isDisabled={disabled}
          onChange={onChange!}
        />
      ),
      [EInputType.Radio]: () => (
        <InputRadio
          radioOptions={radioOptions}
          readOnly={readOnly}
          forcedValue={forcedValue}
          radioExtraStyles={radioExtraStyles}
          checked={checked || null}
        />
      ),
      [EInputType.Select]: () => (
        <InputSelect
          clearable={clearable}
          inputTextType={inputTextType || EInputTextType.Material}
          isDisabled={disabled}
          isFocused={isFocused}
          isLoading={isLoading}
          name={name!}
          onBlur={onBlur}
          onChange={onChange}
          onDropdownOpen={onDropdownOpen!}
          onSelected={onSelected}
          placeholder={placeholder}
          readOnly={readOnly}
          selectOptions={selectOptions}
          type={type!}
          value={value}
          filtering={filtering}
          isOpen={isOpen}
          onFocus={onFocus}
        />
      ),
      [EInputType.Text]: () => (
        <InputText
          isFocused={isFocused}
          isLoading={isLoading}
          name={name}
          min={min}
          max={max}
          step={step}
          maxLength={maxLength}
          value={value}
          required={required}
          placeholder={placeholder}
          type={type}
          readOnly={readOnly}
          isDisabled={disabled}
          inputTextType={inputTextType || EInputTextType.Material}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          filtering={filtering}
          onKeyDown={onKeyDown}
        />
      ),
      [EInputType.File]: () => (
        <InputFile
          id={id}
          isFocused={isFocused}
          isLoading={isLoading}
          name={name}
          value={value}
          file={file}
          placeholder={placeholder}
          type={type}
          readOnly={readOnly}
          isDisabled={disabled}
          onChange={onChange}
        />
      ),
      [EInputType.Textarea]: () => (
        <Textarea
          id={id}
          isFocused={isFocused}
          isLoading={isLoading}
          name={name}
          value={value}
          placeholder={placeholder}
          type={type}
          readOnly={readOnly}
          isDisabled={disabled}
          onChange={onChange}
          cols={cols}
          rows={rows}
        />
      ),
      [EInputType.Date]: () => (
        <InputDate
          name={name}
          value={value}
          minDate={minDate}
          maxDate={maxDate}
          isDisabled={disabled}
          onChange={onChange}
        />
      ),
      [EInputType.Switch]: () => (
        <Switch
          name={name}
          value={value}
          type={switchType}
          size={size}
          isDisabled={disabled}
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={onChange!}
          style={style}
        />
      ),
      [EInputType.Copy]: () => <CopyInput value={value} />,
    });
  }

  private inputType(): EInputType {
    const { type, selectOptions, radioOptions, customComponent } = this.props.inputConfig;
    if (type === 'textarea') {
      return EInputType.Textarea;
    } else if (type === 'file') {
      return EInputType.File;
    } else if (type === 'checkbox') {
      return EInputType.Checkbox;
    } else if (type === 'date') {
      return EInputType.Date;
    } else if (type === 'switch') {
      return EInputType.Switch;
    } else if (defined(selectOptions)) {
      return EInputType.Select;
    } else if (defined(radioOptions)) {
      return EInputType.Radio;
    } else if (defined(customComponent)) {
      return EInputType.Custom;
    } else if (type === 'copy') {
      return EInputType.Copy;
    } else {
      return EInputType.Text;
    }
  }
}
